import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete } from '@material-ui/lab';
import { TextField } from '@material-ui/core';
import trI18n from '@/common/utils/trI18n';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { tr } from '@/common/utils/tr';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import TranslationConstant from '@/common/constant/TranslationConstant';
import { getLanguageDisplayName } from '@/common/utils/translationUtils';
import '@/component/common/LangSetting.css';

const MAX_TARGET_TRANSLATE_LANGS = TranslationConstant.MAX_TARGET_TRANSLATE_LANGS;

// Language options derived from the shared TRANSLATION_LANG_DISPLAY_JP map (source of truth).
// Shape { code, labelJp } is kept so the Autocomplete option handling stays unchanged.
const LANGUAGE_OPTIONS = Object.keys(TranslationConstant.TRANSLATION_LANG_DISPLAY_JP).map(code => ({
    code,
    labelJp: getLanguageDisplayName(code)
}));

/**
 * CR-2026-006: Attachment auto-translation config block in Format Definition.
 * Renders the "添付ファイルを自動翻訳する" toggle + source/target language selectors.
 * Pure presentational component; parent owns the state.
 */
export default function AttachmentAutoTranslateSetting({
    autoTranslate,
    sourceLang,
    targetLangs,
    disabled,
    onChangeAutoTranslate,
    onChangeSourceLang,
    onChangeTargetLangs
}) {
    useTranslationSubscription();
    // Auto-detect label is already translated via trI18n; other labels are literals passed through tr().
    const getLanguageLabel = option => option.labelJp;
    // Source options = auto-detect (empty code) + all supported languages.
    const autoDetectOption = { code: '', labelJp: trI18n('LABEL-TRANSLATE-AUTO-DETECT') };
    const sourceOptions = [autoDetectOption, ...LANGUAGE_OPTIONS];

    // Target options exclude the language currently selected as a specific source (BR-9).
    const targetOptions = LANGUAGE_OPTIONS.filter(item => !sourceLang || item.code !== sourceLang);

    const sourceValue = sourceLang
        ? LANGUAGE_OPTIONS.find(item => item.code === sourceLang) || autoDetectOption
        : autoDetectOption;

    // Preserve user selection order by mapping from targetLangs instead of filtering LANGUAGE_OPTIONS.
    const targetValue = (targetLangs || [])
        .map(code => LANGUAGE_OPTIONS.find(item => item.code === code))
        .filter(Boolean);

    const isTargetLimitReached = (targetLangs || []).length >= MAX_TARGET_TRANSLATE_LANGS;

    const langDisabled = disabled || !autoTranslate;

    // Show required error only after user has interacted with this field.
    const [hasInteracted, setHasInteracted] = useState(false);
    const showTargetRequiredError = hasInteracted && autoTranslate && (targetLangs || []).length === 0;

    const handleChangeTarget = (e, selected) => {
        setHasInteracted(true);
        // Enforce max target languages (BR-4 / VR-2): ignore additions beyond the limit.
        if (selected.length > MAX_TARGET_TRANSLATE_LANGS) {
            return;
        }
        onChangeTargetLangs(selected.map(item => item.code));
    };

    return (
        <div className="attachmentAutoTranslateSetting">
            <div className="setting-title-bottom">
                <span>{trI18n('LABEL-AUTO-TRANSLATE-ATTACHMENT')}</span>
                <div className="switch-check">
                    <input
                        id="attachment_auto_translate"
                        type="checkbox"
                        checked={Boolean(autoTranslate)}
                        disabled={disabled}
                        onChange={e => onChangeAutoTranslate(e.target.checked)}
                    />
                    <label htmlFor="attachment_auto_translate"></label>
                </div>
            </div>

            <div className="userInfoItem langSettingRow sourceLang">
                <div className="userInfoNameLabel langSettingLabel">
                    <span>{trI18n('LABEL-TRANSLATE-SOURCE-LANG')}</span>
                </div>
                <div className="langSettingControl">
                    <Autocomplete
                        options={sourceOptions}
                        getOptionLabel={option => (option ? getLanguageLabel(option) : '')}
                        getOptionSelected={(option, val) => option.code === val.code}
                        value={sourceValue}
                        disableClearable
                        disabled={langDisabled}
                        popupIcon={<ExpandMoreIcon fontSize="small" />}
                        onChange={(e, selected) => {
                            if (!selected) return;
                            onChangeSourceLang(selected.code);
                        }}
                        noOptionsText={trI18n('COMMSG001')}
                        renderInput={params => (
                            <TextField
                                {...params}
                                variant="outlined"
                                className="form-control"
                            />
                        )}
                    />
                </div>
            </div>

            <div className="userInfoItem langSettingRow">
                <div className="userInfoNameLabel langSettingLabel">
                    <span>{trI18n('LABEL-TRANSLATE-TARGET-LANG')}</span>
                    <span className="star-required">*</span>
                </div>
                <div className="langSettingControl">
                    <Autocomplete
                        multiple
                        filterSelectedOptions
                        options={targetOptions}
                        getOptionLabel={option => (option ? getLanguageLabel(option) : '')}
                        getOptionSelected={(option, val) => option.code === val.code}
                        getOptionDisabled={() => isTargetLimitReached}
                        value={targetValue}
                        disabled={langDisabled}
                        popupIcon={<ExpandMoreIcon fontSize="small" />}
                        onChange={handleChangeTarget}
                        noOptionsText={trI18n('COMMSG001')}
                        renderInput={params => (
                            <TextField
                                {...params}
                                variant="outlined"
                                helperText={
                                    isTargetLimitReached
                                        ? trI18n('MSG-TRANSLATE-MAX-TARGET-LANGS')
                                        : showTargetRequiredError
                                            ? trI18n('MSG-TRANSLATE-TARGET-LANG-REQUIRED')
                                            : ''
                                }
                                FormHelperTextProps={{
                                    className: (showTargetRequiredError || isTargetLimitReached) ? 'target-lang-helper-error' : ''
                                }}
                                size="small"
                                className="form-control"
                            />
                        )}
                    />
                </div>
            </div>
        </div>
    );
}

AttachmentAutoTranslateSetting.propTypes = {
    autoTranslate: PropTypes.bool,
    sourceLang: PropTypes.string,
    targetLangs: PropTypes.array,
    disabled: PropTypes.bool,
    onChangeAutoTranslate: PropTypes.func.isRequired,
    onChangeSourceLang: PropTypes.func.isRequired,
    onChangeTargetLangs: PropTypes.func.isRequired
};
